/**
 * TimePicker
 *
 * A dual-select time picker using Mantine Select dropdowns for hour and minute.
 * Produces and consumes "HH:mm" string values, matching the format used by the API.
 */

import { useMemo, useCallback } from 'react';
import { Group, Select, Text, Box, Input } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';

export interface ThemedTimePickerProps {
  /** Current value in "HH:mm" format */
  value?: string;
  /** Called with "HH:mm" string when time changes */
  onChange?: (value: string) => void;
  /** Field label */
  label?: string;
  /** Placeholder shown when no value */
  placeholder?: string;
  /** Error message */
  error?: string | null;
  /** Disable the picker */
  disabled?: boolean;
  /** Whether field is required */
  required?: boolean;
  /** 12-hour or 24-hour format (default: 24) */
  format?: 12 | 24;
  /** Minute interval for the dropdown (default: 5) */
  minuteStep?: number;
}

const TimePicker = ({
  value,
  onChange,
  label,
  error,
  disabled = false,
  required = false,
  format = 24,
  minuteStep = 5,
}: ThemedTimePickerProps) => {
  // Parse current value
  const [currentHour, currentMinute] = useMemo(() => {
    if (!value) return ['', ''];
    const parts = value.split(':');
    if (parts.length < 2) return ['', ''];
    return [parts[0], parts[1]];
  }, [value]);

  // Generate hour options
  const hourOptions = useMemo(() => {
    const maxHour = format === 12 ? 12 : 23;
    const startHour = format === 12 ? 1 : 0;
    const options = [];
    for (let h = startHour; h <= maxHour; h++) {
      const val = String(h).padStart(2, '0');
      options.push({ value: val, label: val });
    }
    return options;
  }, [format]);

  // Generate minute options
  const minuteOptions = useMemo(() => {
    const options = [];
    for (let m = 0; m < 60; m += minuteStep) {
      const val = String(m).padStart(2, '0');
      options.push({ value: val, label: val });
    }
    return options;
  }, [minuteStep]);

  const handleHourChange = useCallback((hour: string | null) => {
    if (!hour) {
      onChange?.('');
      return;
    }
    const min = currentMinute || '00';
    onChange?.(`${hour}:${min}`);
  }, [currentMinute, onChange]);

  const handleMinuteChange = useCallback((minute: string | null) => {
    if (!minute) {
      onChange?.('');
      return;
    }
    const hr = currentHour || '00';
    onChange?.(`${hr}:${minute}`);
  }, [currentHour, onChange]);

  return (
    <Box>
      {label && (
        <Input.Label required={required} mb={4}>
          {label}
        </Input.Label>
      )}
      <Group gap="xs" wrap="nowrap" align="center">
        <Select
          value={currentHour || null}
          onChange={handleHourChange}
          data={hourOptions}
          placeholder="HH"
          disabled={disabled}
          searchable
          allowDeselect={false}
          leftSection={<IconClock size={14} />}
          style={{ flex: 1 }}
          error={!!error}
          styles={{
            input: { textAlign: 'center' },
          }}
        />
        <Text fw={700} size="lg" c="dimmed">:</Text>
        <Select
          value={currentMinute || null}
          onChange={handleMinuteChange}
          data={minuteOptions}
          placeholder="mm"
          disabled={disabled}
          searchable
          allowDeselect={false}
          style={{ flex: 1 }}
          error={!!error}
          styles={{
            input: { textAlign: 'center' },
          }}
        />
      </Group>
      {error && typeof error === 'string' && (
        <Input.Error mt={4}>{error}</Input.Error>
      )}
    </Box>
  );
};

TimePicker.displayName = 'TimePicker';

export default TimePicker;
